<template>
  <v-main>
    <v-container class="max-w-3xl">
      <div class="text-2xl mb-8">{{ t("myAccount.title") }}</div>

      <v-list lines="two" class="bg-transparent py-0">
        <v-list-item :title="t('myAccount.email')" class="px-0 py-5">
          <v-list-item-subtitle class="flex items-center gap-2">
            {{ user.authenticatedUser?.firebaseUser.email }}
            <v-chip
              v-if="user.authenticatedUser?.firebaseUser.emailVerified"
              color="success"
              size="x-small"
              prepend-icon="mdi-check"
            >
              {{ t("myAccount.emailVerified") }}
            </v-chip>
          </v-list-item-subtitle>
          <template #append>
            <v-btn variant="tonal" color="primary" @click="openDialog('email')">
              {{ t("myAccount.change") }}
            </v-btn>
          </template>
        </v-list-item>

        <v-divider />

        <v-list-item :title="t('myAccount.password')" subtitle="••••••••••••" class="px-0 py-5">
          <template #append>
            <v-btn variant="tonal" color="primary" @click="openDialog('password')">
              {{ t("myAccount.change") }}
            </v-btn>
          </template>
        </v-list-item>

        <template v-if="user.isKind('user')">
          <v-divider />

          <v-list-item
            :title="t('myAccount.passkeys')"
            :subtitle="t('myAccount.passkeysDescription')"
            class="px-0 py-5"
          >
            <template #append>
              <v-btn
                variant="tonal"
                color="primary"
                :disabled="scaWalletsLoading || !scaWallets"
                @click="openDialog('addPasskey')"
              >
                {{ t("myAccount.addPasskey") }}
              </v-btn>
            </template>
          </v-list-item>

          <v-skeleton-loader
            v-if="scaWalletsLoading"
            type="list-item-avatar-two-line"
            class="bg-transparent"
          />

          <template v-else>
            <v-list-item
              v-for="scaWallet in scaWallets"
              :key="scaWallet.id"
              :title="scaWallet.provider ?? t('myAccount.passkey')"
              :subtitle="
                scaWallet.creationDate
                  ? t('myAccount.passkeyAddedOn', {
                      date: d(new Date(scaWallet.creationDate), { dateStyle: 'long' }),
                    })
                  : undefined
              "
              prepend-icon="mdi-key-variant"
              class="px-0"
            >
              <template #append>
                <v-btn
                  icon="mdi-delete-outline"
                  variant="text"
                  size="small"
                  :disabled="scaWallets?.length === 1"
                  :title="
                    scaWallets?.length === 1
                      ? t('myAccount.lastPasskey')
                      : t('myAccount.removePasskey')
                  "
                  @click="
                    passkeyToRemove = scaWallet;
                    openDialog('removePasskey');
                  "
                />
              </template>
            </v-list-item>
          </template>
        </template>

        <template v-else>
          <v-divider />

          <v-list-item
            :title="t('myAccount.phone')"
            class="px-0 py-5"
            :subtitle="enrolledPhoneNumber || t('common.none')"
          >
            <template #append>
              <v-btn variant="tonal" color="primary" @click="openDialog('phone')">
                {{ t("myAccount.change") }}
              </v-btn>
            </template>
          </v-list-item>
        </template>
      </v-list>

      <v-btn
        class="mt-10"
        color="error"
        variant="tonal"
        prepend-icon="mdi-logout mdi-flip-h"
        data-testid="sidemenu-logout"
        @click="logout"
      >
        {{ t("sidemenu.logout") }}
      </v-btn>

      <v-dialog :model-value="!!action" max-width="480" persistent>
        <v-card>
          <v-card-title class="flex items-center">
            <span v-if="action">{{ t(DIALOG_TITLES[action]) }}</span>
            <v-spacer />
            <v-btn
              icon="mdi-close"
              variant="text"
              size="small"
              :disabled="loading"
              @click="action = null"
            />
          </v-card-title>

          <v-card-text class="px-4">
            <ReauthenticateStep
              v-if="step === 'reauth'"
              v-model:loading="loading"
              :recaptcha-container="recaptchaContainer"
              @done="step = 'form'"
            />

            <v-form
              v-else-if="step === 'form'"
              id="account-form"
              ref="form"
              :disabled="loading"
              validate-on="invalid-input lazy"
              @submit.prevent="submitForm"
            >
              <v-text-field
                v-if="action === 'email'"
                v-model.trim="newEmail"
                :rules="[required, validEmail]"
                :label="t('myAccount.newEmail')"
                type="email"
                autocomplete="email"
                variant="outlined"
                class="mb-2"
              />
              <template v-else-if="action === 'password'">
                <v-text-field
                  v-model="newPassword"
                  :rules="[required, minLength(12)]"
                  :label="t('myAccount.newPassword')"
                  :type="showPassword ? 'text' : 'password'"
                  :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  autocomplete="new-password"
                  variant="outlined"
                  class="mb-2"
                  @click:append-inner="showPassword = !showPassword"
                />
                <v-text-field
                  v-model="confirmPassword"
                  :rules="[
                    required,
                    (value: string) => value === newPassword || t('myAccount.passwordsDoNotMatch'),
                  ]"
                  :label="t('myAccount.confirmPassword')"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  variant="outlined"
                  class="mb-2"
                />
              </template>
              <v-text-field
                v-else-if="action === 'phone'"
                v-model.trim="newPhoneNumber"
                :rules="[required, validPhoneNumber]"
                :label="t('myAccount.newPhone')"
                :placeholder="t('mfa.phoneNumberPlaceholder')"
                persistent-placeholder
                autocomplete="tel"
                variant="outlined"
                class="mb-2"
              />
              <div v-else-if="action === 'addPasskey'" class="text-sm mb-4">
                {{ t("myAccount.addPasskeyDescription") }}
              </div>
              <template v-else>
                <div class="text-sm mb-4">{{ t("myAccount.removePasskeyDescription") }}</div>
                <div class="text-sm font-medium">
                  {{ passkeyToRemove?.provider ?? t("myAccount.passkey") }}
                </div>
                <div v-if="passkeyToRemove?.creationDate" class="text-sm">
                  {{
                    t("myAccount.passkeyAddedOn", {
                      date: d(new Date(passkeyToRemove.creationDate), { dateStyle: "long" }),
                    })
                  }}
                </div>
              </template>
            </v-form>

            <v-form
              v-else-if="step === 'phoneCode'"
              id="account-form"
              ref="form"
              :disabled="loading"
              validate-on="invalid-input lazy"
              @submit.prevent="submitPhoneCode"
            >
              <div class="text-sm mb-4">
                {{ t("mfa.verificationCodeSentTo", { phoneNumber: newPhoneNumber }) }}
              </div>
              <v-text-field
                v-model.trim="phoneCode"
                :rules="[required]"
                autocomplete="one-time-code"
                :label="t('mfa.phoneNumberVerificationCode')"
                maxlength="6"
                inputmode="numeric"
                variant="outlined"
                class="mb-2"
              />
            </v-form>

            <v-alert v-else type="success" density="compact">
              {{ t("myAccount.emailChangeSent", { email: newEmail }) }}
            </v-alert>

            <v-alert v-if="error && step !== 'reauth'" density="compact" color="error" type="error">
              {{ error }}
            </v-alert>
          </v-card-text>

          <v-divider />

          <v-card-actions class="px-4 py-3">
            <v-btn v-if="step !== 'emailSent'" :disabled="loading" @click="action = null">
              {{ t("common.cancel") }}
            </v-btn>
            <v-spacer />
            <v-btn
              v-if="step === 'emailSent'"
              color="primary"
              variant="flat"
              @click="action = null"
            >
              {{ t("common.close") }}
            </v-btn>
            <v-btn
              v-else
              type="submit"
              :form="step === 'reauth' ? 'account-reauth-form' : 'account-form'"
              :color="action === 'removePasskey' && step === 'form' ? 'error' : 'primary'"
              variant="flat"
              :disabled="loading"
              :loading="loading"
            >
              {{
                step === "reauth"
                  ? user.isKind("user")
                    ? t("login.treezorScaSignIn")
                    : t("common.next")
                  : step === "form" && action === "phone"
                    ? t("mfa.submitPhoneNumber")
                    : step === "form" && action === "addPasskey"
                      ? t("myAccount.addPasskey")
                      : step === "form" && action === "removePasskey"
                        ? t("myAccount.removePasskey")
                        : t("common.save")
              }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <div ref="recaptchaContainer"></div>
    </v-container>
  </v-main>
</template>

<script setup lang="ts">
import { useI18n, type MessageKey } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { call } from "@/firebase";
import { useToast, useUserStore } from "@/store";
import { useFirebaseErrorMessage } from "@/utils/errors";
import { verifyPhoneNumber } from "@/utils/recaptcha";
import { startRegistration, WebAuthnError } from "@/utils/webAuthn";
import type { CallablesMap } from "@aircap/common/callables";
import { captureException } from "@sentry/vue";
import { FirebaseError } from "firebase/app";
import {
  AuthErrorCodes,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  updatePassword,
  type PhoneMultiFactorInfo,
} from "firebase/auth";
import { nextTick, onMounted, ref, watch } from "vue";
import { useFirebaseAuth } from "vuefire";
import type { VForm } from "vuetify/components";
import ReauthenticateStep from "./ReauthenticateStep.vue";

const RECENT_SIGN_IN_MS = 4 * 60 * 1000;

type Action = "email" | "password" | "phone" | "addPasskey" | "removePasskey";
type ScaWallet = CallablesMap["getTreezorSCAWallets"]["response"]["scaWallets"][number];

const DIALOG_TITLES: Record<Action, MessageKey> = {
  email: "myAccount.changeEmail",
  password: "myAccount.changePassword",
  phone: "myAccount.changePhone",
  addPasskey: "myAccount.addPasskey",
  removePasskey: "myAccount.removePasskey",
};

const { t, d } = useI18n();
const { currentLanguage } = useLanguage();
const user = useUserStore();
const auth = useFirebaseAuth();
const { required, validEmail, validPhoneNumber, minLength } = useRules();
const { showErrorToast, showSuccessToast, showWarningToast } = useToast();
const { getFirebaseErrorMessage } = useFirebaseErrorMessage();

const action = ref<Action | null>(null);
const step = ref<"reauth" | "form" | "phoneCode" | "emailSent">("reauth");
const loading = ref(false);
const error = ref<string | null>(null);
const form = ref<VForm>();
const newEmail = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const showPassword = ref(false);
const newPhoneNumber = ref("");
const phoneCode = ref("");
const phoneVerificationId = ref("");
const enrolledPhoneNumber = ref<string>();
const recaptchaContainer = ref<HTMLElement>();
const scaWallets = ref<ScaWallet[]>();
const scaWalletsLoading = ref(false);
const passkeyToRemove = ref<ScaWallet>();

const loadScaWallets = async () => {
  scaWalletsLoading.value = true;
  try {
    scaWallets.value = (await call("getTreezorSCAWallets")({})).scaWallets;
  } catch (cause) {
    const error = new Error("Error loading Treezor SCA wallets", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    scaWalletsLoading.value = false;
  }
};

onMounted(async () => {
  if (user.isKind("user")) await loadScaWallets();
});

watch(
  () => user.authenticatedUser?.idToken,
  () => {
    if (!auth?.currentUser) return;
    enrolledPhoneNumber.value = multiFactor(auth.currentUser)
      .enrolledFactors.filter(
        (factor): factor is PhoneMultiFactorInfo =>
          factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
      )
      .map((factor) => factor.phoneNumber)
      .join(", ");
  },
  { immediate: true },
);

const openDialog = (newAction: Action) => {
  newEmail.value = "";
  newPassword.value = "";
  confirmPassword.value = "";
  newPhoneNumber.value = "";
  phoneCode.value = "";
  error.value = null;
  const authTime = user.authenticatedUser
    ? new Date(user.authenticatedUser.idToken.authTime).getTime()
    : 0;
  step.value = Date.now() - authTime < RECENT_SIGN_IN_MS ? "form" : "reauth";
  action.value = newAction;
};

const handleActionError = (name: string, cause: unknown) => {
  loading.value = false;
  if (
    cause instanceof FirebaseError &&
    (cause.code === AuthErrorCodes.CREDENTIAL_TOO_OLD_LOGIN_AGAIN ||
      cause.code === "functions/failed-precondition")
  ) {
    step.value = "reauth";
    return;
  }
  if (
    cause instanceof WebAuthnError &&
    cause.code === "ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED"
  ) {
    error.value = t("myAccount.passkeyAlreadyRegistered");
    return;
  }
  const wrappedError = new Error(`Error in Account ${name}`, { cause });
  console.error(wrappedError);
  captureException(wrappedError);
  if (cause instanceof FirebaseError && cause.code === "functions/already-exists")
    error.value = t("myAccount.emailAlreadyInUse");
  else
    error.value =
      cause instanceof FirebaseError ? getFirebaseErrorMessage(cause) : t("common.error");
};

const submitForm = async () => {
  error.value = null;
  try {
    const validation = await form.value?.validate();
    if (!validation?.valid) return;
    if (!auth?.currentUser) throw new Error("User is not authenticated");
    if (!action.value) throw new Error("No account action selected");
    loading.value = true;

    switch (action.value) {
      case "email":
        await call("sendUserEmailChangeEmail")({
          newEmail: newEmail.value,
          locale: currentLanguage.value.code,
        });
        step.value = "emailSent";
        break;
      case "password":
        await updatePassword(auth.currentUser, newPassword.value);
        showSuccessToast(t("myAccount.passwordUpdated"));
        action.value = null;
        break;
      case "phone": {
        if (!recaptchaContainer.value) throw new Error("reCAPTCHA container is not mounted");
        const session = await multiFactor(auth.currentUser).getSession();
        phoneVerificationId.value = await verifyPhoneNumber(auth, recaptchaContainer.value, {
          phoneNumber: newPhoneNumber.value,
          session,
        });
        step.value = "phoneCode";
        break;
      }
      case "addPasskey": {
        const { webAuthnRegistrationOptions } = await call("enrollTreezorSCAPayload")({});
        const webAuthnRegistrationResponse = await startRegistration({
          optionsJSON: webAuthnRegistrationOptions,
        });
        await call("enrollTreezorSCA")({ webAuthnRegistrationResponse });
        await loadScaWallets();
        showSuccessToast(t("myAccount.passkeyAdded"));
        action.value = null;
        break;
      }
      case "removePasskey": {
        if (!passkeyToRemove.value) throw new Error("No passkey selected");
        const { rpId, publicKeyCredentialIds } = await call("deleteTreezorSCAWallet")({
          scaWalletId: passkeyToRemove.value.id,
        });
        await loadScaWallets();
        showSuccessToast(t("myAccount.passkeyRemoved"));
        action.value = null;
        // Lets the password manager hide the removed passkey. Not every browser supports it.
        if ("signalUnknownCredential" in PublicKeyCredential)
          for (const credentialId of publicKeyCredentialIds)
            await PublicKeyCredential.signalUnknownCredential({ rpId, credentialId }).catch(
              (cause: unknown) => {
                const error = new Error("Error signaling removed passkey", { cause });
                console.error(error);
                captureException(error);
              },
            );
        break;
      }
    }
    loading.value = false;
  } catch (cause) {
    handleActionError("submitForm", cause);
  }
};

const submitPhoneCode = async () => {
  error.value = null;
  try {
    const validation = await form.value?.validate();
    if (!validation?.valid) return;
    if (!auth?.currentUser) throw new Error("User is not authenticated");
    loading.value = true;

    const { claims } = await auth.currentUser.getIdTokenResult();
    let signInFactorUid: unknown;
    if (claims.firebase && "second_factor_identifier" in claims.firebase)
      signInFactorUid = claims.firebase.second_factor_identifier;
    // Unenrolling the factor this session signed in with revokes the session, so it goes last.
    const phoneFactors = multiFactor(auth.currentUser).enrolledFactors.filter(
      (factor) => factor.factorId === PhoneMultiFactorGenerator.FACTOR_ID,
    );
    const previousPhoneFactors = [
      ...phoneFactors.filter((factor) => factor.uid !== signInFactorUid),
      ...phoneFactors.filter((factor) => factor.uid === signInFactorUid),
    ];
    await multiFactor(auth.currentUser).enroll(
      PhoneMultiFactorGenerator.assertion(
        PhoneAuthProvider.credential(phoneVerificationId.value, phoneCode.value),
      ),
    );
    for (const [index, factor] of previousPhoneFactors.entries()) {
      try {
        await multiFactor(auth.currentUser).unenroll(factor);
      } catch (cause) {
        if (
          index === previousPhoneFactors.length - 1 &&
          cause instanceof FirebaseError &&
          cause.code === AuthErrorCodes.TOKEN_EXPIRED
        ) {
          showWarningToast(t("myAccount.phoneChangeSignInAgain"));
          await auth.signOut();
          return;
        }
        throw cause;
      }
    }
    await user.refresh();

    showSuccessToast(t("myAccount.phoneUpdated"));
    loading.value = false;
    action.value = null;
  } catch (cause) {
    handleActionError("submitPhoneCode", cause);
  }
};

const logout = async () => {
  try {
    await auth?.signOut();
  } catch (cause) {
    const error = new Error("Error signing out", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  }
};

watch(phoneCode, async (code) => {
  if (loading.value || !/^\d{6}$/.test(code)) return;
  await nextTick();
  void submitPhoneCode();
});
</script>

<style lang="css">
.grecaptcha-badge {
  visibility: hidden;
}
</style>
